/* RL Favorite — tabs switcher.
   Vanilla, no module. Prefixed globals only.

   Tab state is persisted in history.state so the active tab survives
   navigation away (e.g. to a PDP) and is restored when the user hits
   the browser back button. Indexed per section-id to support multiple
   Favorite sections on the same page (rare, but cheap).

   History strategy : pushState on tab click — each tab switch creates
   a new history entry, so the user can back/forward THROUGH the tabs
   they visited before leaving the page. The first/initial state is
   replaceState (anchors the current entry, doesn't pollute). popstate
   restores the matching tab without scrolling. */
(function () {
  'use strict';

  var RL_FAVORITE_STATE_KEY = 'rlFavoriteTabs';

  function RL_FAVORITE_getSectionId(section) {
    return section.getAttribute('data-rl-favorite') ||
           section.getAttribute('id') ||
           '';
  }

  function RL_FAVORITE_buildNextState(section, tabId) {
    var sectionId = RL_FAVORITE_getSectionId(section);
    if (!sectionId) return null;
    var current = window.history.state || {};
    var tabs = current[RL_FAVORITE_STATE_KEY] || {};
    // Clone to avoid mutating the live state object (some browsers
    // freeze it ; mutating in place silently no-ops).
    var nextTabs = {};
    for (var t in tabs) { if (Object.prototype.hasOwnProperty.call(tabs, t)) nextTabs[t] = tabs[t]; }
    nextTabs[sectionId] = tabId;
    var next = {};
    for (var k in current) { if (Object.prototype.hasOwnProperty.call(current, k)) next[k] = current[k]; }
    next[RL_FAVORITE_STATE_KEY] = nextTabs;
    return next;
  }

  // Initial-state anchor : if the current history entry has no
  // rlFavoriteTabs payload, write the default-active tab on it so
  // back-from-PDP restores correctly even if the user never clicked
  // a tab. Uses replaceState (no new history entry).
  function RL_FAVORITE_anchorInitialState(section) {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    var current = window.history.state || {};
    var sectionId = RL_FAVORITE_getSectionId(section);
    if (!sectionId) return;
    if (current[RL_FAVORITE_STATE_KEY] && current[RL_FAVORITE_STATE_KEY][sectionId]) return;
    var activeTab = section.querySelector('[data-rl-tab].is-active');
    if (!activeTab) return;
    var tabId = activeTab.getAttribute('data-rl-tab');
    var next = RL_FAVORITE_buildNextState(section, tabId);
    if (!next) return;
    try {
      window.history.replaceState(next, '', window.location.href);
    } catch (_e) {}
  }

  // pushState on each tab click — creates a back-stack entry per
  // tab switch so the user can back/forward through their tab path.
  function RL_FAVORITE_persistTab(section, tabId) {
    if (!window.history || typeof window.history.pushState !== 'function') return;
    var next = RL_FAVORITE_buildNextState(section, tabId);
    if (!next) return;
    try {
      window.history.pushState(next, '', window.location.href);
    } catch (_e) {
      // Some embedded contexts (theme editor preview iframe) may throw;
      // failure is non-blocking — tabs still work, just no persistence.
    }
  }

  function RL_FAVORITE_restoreTab(section) {
    var state = window.history && window.history.state;
    if (!state || !state[RL_FAVORITE_STATE_KEY]) return;
    var sectionId = RL_FAVORITE_getSectionId(section);
    var tabId = state[RL_FAVORITE_STATE_KEY][sectionId];
    if (!tabId) return;
    // Only restore if the tab actually exists in this section.
    var target = section.querySelector('[data-rl-tab="' + tabId + '"]');
    if (target) RL_FAVORITE_activateTab(section, tabId, true);
  }

  function RL_FAVORITE_activateTab(section, tabId, skipScroll) {
    var tabs = section.querySelectorAll('[data-rl-tab]');
    var panels = section.querySelectorAll('[role="tabpanel"]');
    var i;

    for (i = 0; i < tabs.length; i++) {
      var tab = tabs[i];
      var isActive = tab.getAttribute('data-rl-tab') === tabId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive && !skipScroll) {
        // Scroll the active tab into view on mobile horizontal scroll.
        // Skipped on restore-from-history so popstate doesn't yank the
        // viewport while the browser is restoring scroll position.
        if (typeof tab.scrollIntoView === 'function') {
          tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    }

    for (i = 0; i < panels.length; i++) {
      var panel = panels[i];
      var panelId = panel.getAttribute('id') || '';
      var matches = panelId === 'rl-favorite-panel-' + tabId;
      panel.classList.toggle('is-active', matches);
      if (matches) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    }
  }

  function RL_FAVORITE_initSection(section) {
    var tabs = section.querySelectorAll('[data-rl-tab]');
    if (!tabs.length) return;

    for (var i = 0; i < tabs.length; i++) {
      tabs[i].addEventListener('click', function (e) {
        var tabId = e.currentTarget.getAttribute('data-rl-tab');
        if (!tabId) return;
        // Skip if the click targets the already-active tab — avoids
        // creating a no-op history entry.
        if (e.currentTarget.classList.contains('is-active')) return;
        RL_FAVORITE_activateTab(section, tabId);
        RL_FAVORITE_persistTab(section, tabId);
      });

      tabs[i].addEventListener('keydown', function (e) {
        // Left/Right arrow navigation through the tablist.
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var current = section.querySelector('[data-rl-tab].is-active');
        if (!current) return;
        var list = Array.prototype.slice.call(tabs);
        var idx = list.indexOf(current);
        var next = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
        if (next < 0) next = list.length - 1;
        if (next >= list.length) next = 0;
        var nextId = list[next].getAttribute('data-rl-tab');
        if (nextId) {
          RL_FAVORITE_activateTab(section, nextId);
          RL_FAVORITE_persistTab(section, nextId);
          list[next].focus();
        }
      });
    }
  }

  function RL_FAVORITE_initAll() {
    var sections = document.querySelectorAll('[data-rl-favorite]');
    for (var i = 0; i < sections.length; i++) {
      RL_FAVORITE_initSection(sections[i]);
      RL_FAVORITE_restoreTab(sections[i]);
      RL_FAVORITE_anchorInitialState(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', RL_FAVORITE_initAll);
  } else {
    RL_FAVORITE_initAll();
  }

  // Browser back/forward — restore the tab state saved on the
  // history entry being navigated to. Fires after history.state has
  // already been swapped to the destination entry.
  window.addEventListener('popstate', function () {
    var sections = document.querySelectorAll('[data-rl-favorite]');
    for (var i = 0; i < sections.length; i++) {
      RL_FAVORITE_restoreTab(sections[i]);
    }
  });

  // Re-init on Shopify theme editor section reload.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-rl-favorite]')) {
      RL_FAVORITE_initAll();
    }
  });
})();
